import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const questions = [
  {
    id: 1,
    question: "What draws you in first?",
    options: ["Depth & Mystery", "Light & Elegance", "Bold Contrast", "Subtle Harmony"],
  },
  {
    id: 2,
    question: "Your evening ritual?",
    options: ["A glass of aged red", "Champagne by candlelight", "Espresso, neat", "Herbal tea, no rush"],
  },
  {
    id: 3,
    question: "Which texture speaks to you?",
    options: ["Velvet darkness", "Silk & cream", "Crisp & layered", "Smooth & molten"],
  },
];

const results = {
  "Depth & Mystery": { pair: "Rose & Ember", desc: "Persian rose milk paired with smoked espresso dark. Soft romance meets charred intensity." },
  "Light & Elegance": { pair: "Noir & Ivoire", desc: "Our signature — 72% single-origin dark alongside Tahitian vanilla white. The classic yin and yang." },
  "Bold Contrast": { pair: "Fuego & Frost", desc: "Habanero chili dark with cool peppermint cream. Fire meets ice in one unforgettable box." },
  "Subtle Harmony": { pair: "Noir & Ivoire", desc: "Where brooding cacao meets silken vanilla. Two halves of one perfect whole." },
};

export default function FindYourPair() {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [started, setStarted] = useState(false);

  const progress = started ? ((currentQ + 1) / questions.length) * 100 : 0;

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option];
    setAnswers(newAnswers);

    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setShowResult(true);
    }
  };

  const getResult = () => {
    const firstAnswer = answers[0] || "Bold Contrast";
    return results[firstAnswer] || results["Bold Contrast"];
  };

  const reset = () => {
    setCurrentQ(0);
    setAnswers([]);
    setShowResult(false);
    setStarted(false);
  };

  return (
    <section
      id="find-your-pair"
      data-testid="find-your-pair-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]"
    >
      <div className="max-w-2xl mx-auto text-center">
        {/* Section header */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease }}
          className="font-body text-xs md:text-sm uppercase tracking-[0.2em] text-[#C6A96B] mb-6"
        >
          A Bespoke Consultation
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.2, ease }}
          className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#FDFBF7] mb-6"
        >
          Find Your Pair
        </motion.h2>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: 60 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4, ease }}
          className="h-px bg-[#C6A96B] mx-auto mb-16"
        />

        {!started ? (
          /* Start state */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.6, ease }}
          >
            <p className="font-body text-sm md:text-base font-light text-[#A3A3A3] mb-12 max-w-md mx-auto leading-relaxed">
              Three questions. One perfect pair. Discover the two chocolates meant for you.
            </p>
            <button
              data-testid="quiz-start-btn"
              onClick={() => setStarted(true)}
              className="btn-luxury"
            >
              <span>Begin</span>
            </button>
          </motion.div>
        ) : !showResult ? (
          /* Quiz questions */
          <div>
            {/* Progress bar */}
            <div className="w-full h-px bg-[#1F1F1F] mb-16 relative">
              <motion.div
                className="absolute top-0 left-0 h-full bg-[#C6A96B]"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease }}
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentQ}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.8, ease }}
              >
                <p className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-8">
                  {currentQ + 1} / {questions.length}
                </p>
                <h3
                  data-testid={`quiz-question-${currentQ + 1}`}
                  className="font-heading text-3xl md:text-4xl font-light text-[#FDFBF7] mb-12"
                >
                  {questions[currentQ].question}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {questions[currentQ].options.map((option, i) => (
                    <motion.button
                      key={option}
                      data-testid={`quiz-option-${currentQ + 1}-${i + 1}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1, duration: 0.5, ease }}
                      onClick={() => handleAnswer(option)}
                      className="quiz-option text-left"
                    >
                      {option}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          /* Result */
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease }}
            data-testid="quiz-result"
            className="py-8"
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C6A96B] mb-8">
              Your Perfect Pair
            </p>
            <h3 className="font-heading text-4xl md:text-5xl font-light text-[#FDFBF7] mb-6 gold-glow">
              {getResult().pair}
            </h3>
            <div className="w-12 h-px bg-[#C6A96B] mx-auto mb-8" />
            <p className="font-body text-base font-light text-[#A3A3A3] mb-12 max-w-sm mx-auto leading-relaxed">
              {getResult().desc}
            </p>
            <div className="flex items-center justify-center gap-6">
              <button
                data-testid="quiz-discover-btn"
                className="btn-luxury"
              >
                <span>Discover</span>
              </button>
              <button
                data-testid="quiz-retake-btn"
                onClick={reset}
                className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
              >
                Retake
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
